import type { Project } from './types';
import { daysBetween, formatCalendarDate, parseCalendarDate } from './utils';

export function getProjectDateRange(project: Project): { start: string; end: string } | null {
  const start = project.startDate || project.targetDate;
  const end = project.endDate || start;
  if (!start || !end) return null;
  return { start, end };
}

export function getProjectWeekCount(project: Project): number {
  const range = getProjectDateRange(project);
  if (!range) return 0;
  const start = parseCalendarDate(range.start);
  const end = parseCalendarDate(range.end);
  const totalDays = Math.max(1, daysBetween(start, end) + 1);
  return Math.ceil(totalDays / 7);
}

export function formatProjectDate(project: Project): string {
  const range = getProjectDateRange(project);
  if (!range) return '—';

  const start = parseCalendarDate(range.start);
  const end = parseCalendarDate(range.end);
  const sameDay = range.start === range.end;

  if (sameDay) {
    return formatCalendarDate(start, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const sameYear = start.getUTCFullYear() === end.getUTCFullYear();
  if (sameYear) {
    return `${formatCalendarDate(start, { month: 'short', day: 'numeric' })}–${formatCalendarDate(end, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }

  return `${formatCalendarDate(start, { month: 'short', day: 'numeric', year: 'numeric' })}–${formatCalendarDate(end, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

export function formatProjectDateShort(project: Project): string {
  const range = getProjectDateRange(project);
  if (!range) return '—';

  const start = parseCalendarDate(range.start);
  const end = parseCalendarDate(range.end);
  const sameDay = range.start === range.end;

  if (sameDay) return formatCalendarDate(start, { month: 'short', day: 'numeric' });

  return `${formatCalendarDate(start, { month: 'short', day: 'numeric' })}–${formatCalendarDate(end, { month: 'short', day: 'numeric' })}`;
}

export function formatProjectWeekSpan(project: Project): string | null {
  const weeks = getProjectWeekCount(project);
  if (weeks <= 1) return null;
  return `${weeks} weeks`;
}
